import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Column, EmptyState, FlexBox, IconButton, Portal, Text, } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useEffect, useState } from 'react';
import { analytics } from 'app/utils/segment';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { searchActions } from '../../search.actions';
import { FilterAddCondition } from './components/addCondition/filterAddCondition';
import { FilterCondition } from './components/condition/filterCondition';
import { FilterStrategy } from './components/strategy/filterStrategy';
import { useSearchFilters } from './searchFilters.context';
import styles from './searchFilters.module.scss';
import { filtersInitialState } from './utils/definitions';
/** Represents a component that is used visualize the search filters on a sidebar */
export const SearchFilters = () => {
    const dispatch = useAppDispatch();
    const { params } = useAppSelector((state) => state.search);
    const [open, setOpen] = useState(false);
    const { strategy, conditions, validation: { isEmpty, isValid, validate, clearErrors }, } = useSearchFilters();
    // The total count of active filter conditions.
    const activeCount = params.filters.conditions.length;
    // The total count of draft filter conditions.
    const draftCount = conditions.length;
    const isSmallScreen = useBreakpointValue({ base: true, md: false });
    /**
     * When the last filter condition is removed, we clear the filters from the
     * store.
     */
    useEffect(() => {
        if (params.filters.conditions.length && !conditions.length) {
            clearFilters();
        }
    }, [conditions]);
    /** Handles opening and closing the sidebar and sends the tracking events. */
    const handleOpen = (newValue) => {
        setOpen(newValue);
        const trackingProps = { count: activeCount };
        const trackingFn = newValue
            ? analytics.searchFiltersOpened
            : analytics.searchFiltersClosed;
        trackingFn(trackingProps);
    };
    /** If valid, it updates the store with the new filters. */
    const applyFilters = () => {
        const isValid = validate();
        if (isValid) {
            dispatch(searchActions.params.update({
                filters: { strategy, conditions },
                page: 1,
            }));
            analytics.searchFiltersApplied({
                count: draftCount,
                hasKeyword: !!params.query,
            });
            if (isSmallScreen) {
                handleOpen(false);
            }
        }
    };
    /** Restores the filters store to its initial value. */
    const clearFilters = () => {
        clearErrors();
        dispatch(searchActions.params.update({
            filters: filtersInitialState,
            page: 1,
            sort: undefined,
            strict: false,
        }));
        if (!params.query) {
            dispatch(searchActions.savedSearch.close());
        }
        analytics.searchFiltersCleared({ count: activeCount });
        if (isSmallScreen) {
            handleOpen(false);
        }
    };
    const baseButtonProps = {
        variant: 'secondary',
        onClick: () => handleOpen(true),
    };
    return (_jsxs(_Fragment, { children: [isSmallScreen ? (_jsx(IconButton, Object.assign({}, baseButtonProps, { icon: "filter", className: classnames(styles.iconButton, {
                    [styles.iconButtonActive]: activeCount > 0,
                }) }))) : (_jsx(Button, Object.assign({}, baseButtonProps, { label: activeCount || undefined }, { children: "Filters" }))), _jsx(Portal, Object.assign({ containerId: "app" }, { children: _jsx("aside", Object.assign({ className: classnames(styles.sidebar, {
                        [styles.sidebarOpen]: open,
                    }), "aria-hidden": !open, "data-testid": "sidebar" }, { children: _jsxs(Column, { children: [_jsxs(Column.Row, Object.assign({ slottable: true, noMinHeight: true }, { children: [_jsxs(Column.Slot, Object.assign({ placement: "start" }, { children: [_jsx(Text.Heading, Object.assign({ variant: "heading-3" }, { children: "Filters" })), !isEmpty && _jsx(FilterStrategy, {})] })), _jsx(Column.Slot, Object.assign({ placement: "end" }, { children: _jsx(Button, Object.assign({ variant: "tertiary", onClick: () => handleOpen(false) }, { children: "Close" })) }))] })), _jsxs(Column.Row, Object.assign({ scrollable: true, className: styles.conditions }, { children: [isEmpty && (_jsxs(FlexBox, Object.assign({ flexDirection: "column", alignItems: "center", gap: "300" }, { children: [_jsx(EmptyState, { icon: "filter", title: "No filters added yet", description: "Add your own filters to control what data you\u2019d like to include (and exclude) from your results." }), _jsx(FilterAddCondition, Object.assign({ primary: true }, { children: "Add filter" }))] }))), conditions.map((condition, index) => (_jsx(FilterCondition, { index: index, condition: condition }, index))), !isEmpty && (_jsx(FilterAddCondition, { children: "Add more filters" }))] })), !isEmpty && (_jsxs(Column.Row, Object.assign({ slottable: true, noMinHeight: true }, { children: [_jsx(Column.Slot, Object.assign({ placement: "start" }, { children: _jsx(Button, Object.assign({ variant: "tertiary", onClick: clearFilters }, { children: "Clear all" })) })), _jsx(Column.Slot, Object.assign({ placement: "end" }, { children: _jsx(Button, Object.assign({ variant: "primary", onClick: applyFilters, disabled: !isValid }, { children: "Apply filters" })) }))] })))] }) })) }))] }));
};
